/**
 * Overview — bird's-eye landing screen for the founder.
 *
 * Sections:
 *   1. "This week" — 4 stat cards (closing this week, pending verifications,
 *       days-to-verify, disbursed this month).
 *   2. "Right now" — 2-up snapshot of the verification queue + recent donations.
 *   3. "Lifetime numbers" — running totals that will eventually surface on the
 *       public landing page as trust counters.
 *   4. "Top donors" — this month + this year leaderboards.
 */

import { useEffect, useState } from "react";
import { CircleAlert, HandHeart, Wallet, GraduationCap, ExternalLink, Flag } from "lucide-react";
import { StatCard, SectionHeader, StatusBadge, KpiLine, AdminButton } from "./_ui";
import { ADMIN_METRICS, PENDING_VERIFICATIONS, RECENT_DONATIONS } from "../../data/adminMock";
import { getReports, subscribeReports } from "../../lib/wallReports";

const fmt$ = (n) => `$${Number(n).toLocaleString()}`;

// "3 h ago" style formatter used for the flagged snapshot.
const relTime = (iso) => {
  if (!iso) return "—";
  const diffMin = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (diffHr < 24) return `${diffHr} h ago`;
  return `${Math.round(diffHr / 24)} d ago`;
};

export const AdminOverview = ({ onNavigate }) => {
  const pendingPreview = PENDING_VERIFICATIONS.slice(0, 4);
  const donationsPreview = RECENT_DONATIONS.slice(0, 5);

  // Live-subscribe to the wall-reports store so the flagged snapshot stays
  // in sync when a new report lands (or an admin resolves one in another tab).
  const [reportsStore, setReportsStore] = useState(getReports());
  useEffect(() => subscribeReports(setReportsStore), []);
  const flaggedPending = Object.values(reportsStore)
    .filter((r) => !r.resolved)
    .sort((a, b) => {
      const at = new Date(b.reports[b.reports.length - 1].at) - new Date(a.reports[a.reports.length - 1].at);
      return at;
    })
    .slice(0, 3);

  return (
    <div className="space-y-16">
      {/* Welcome */}
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">
          {new Date().toLocaleDateString(undefined, {
            weekday: "long", month: "long", day: "numeric",
          })}
        </p>
        <h1 className="mt-2 font-serif text-4xl leading-tight text-ink lg:text-5xl">
          Good afternoon, Terri.
        </h1>
        <p className="mt-3 max-w-2xl text-base text-ink/70">
          {ADMIN_METRICS.pendingVerificationCount} students are waiting for verification and{" "}
          {ADMIN_METRICS.campaignsClosingThisWeek} campaigns close this week. Here's where things stand.
        </p>
      </div>

      {/* 1 — this week */}
      <section className="space-y-6">
        <SectionHeader eyebrow="This week" title="At a glance" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            testid="stat-closing"
            label="Campaigns closing"
            value={ADMIN_METRICS.campaignsClosingThisWeek}
            accent="tangerine"
            helper="Reach out to students at risk of missing goal."
          />
          <StatCard
            testid="stat-pending"
            label="Pending verifications"
            value={ADMIN_METRICS.pendingVerificationCount}
            accent="cobalt"
            helper={`Avg. days to verify: ${ADMIN_METRICS.daysToVerifyAvg}`}
          />
          <StatCard
            testid="stat-disbursed-month"
            label="Granted this month"
            value={fmt$(ADMIN_METRICS.disbursedThisMonth)}
            accent="grass"
            helper="Weekly disbursements to verified students."
          />
          <StatCard
            testid="stat-active"
            label="Active campaigns"
            value={ADMIN_METRICS.activeCampaignCount}
            accent="ink"
            helper="Live student campaigns across the platform."
          />
        </div>
      </section>

      {/* 2 — Right now (queue snapshot + flagged + donations feed) */}
      <section className="space-y-6">
        <SectionHeader eyebrow="Right now" title="Live operations" />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          {/* Left column: Verifications + Flagged messages, stacked. */}
          <div className="space-y-6">
            {/* Verification queue snapshot */}
            <div className="rounded-lg border border-ink/10 bg-sand">
              <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4">
                <div className="flex items-center gap-2 text-ink">
                  <CircleAlert className="h-4 w-4 text-tangerine" strokeWidth={1.75} />
                  <p className="text-sm font-semibold uppercase tracking-wide">Verification queue</p>
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate("verifications")}
                  className="inline-flex items-center gap-1 text-xs font-medium text-ink hover:text-tangerine"
                  data-testid="overview-view-verifications"
                >
                  Open queue <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                </button>
              </div>
              <ul className="divide-y divide-ink/10">
                {pendingPreview.length === 0 ? (
                  <li className="px-6 py-8 text-center">
                    <p className="font-serif text-lg text-ink">Queue clear.</p>
                    <p className="mt-1 text-xs text-ink/60">
                      New student verifications will appear here once campaigns are submitted.
                    </p>
                  </li>
                ) : (
                  pendingPreview.map((v) => (
                    <li key={v.id} className="flex items-center justify-between gap-4 px-6 py-4">
                      <div className="min-w-0">
                        <p className="truncate font-medium text-ink">{v.name}</p>
                        <p className="mt-0.5 truncate text-xs text-ink/60">
                          {v.school} · Goal {fmt$(v.goal)}
                        </p>
                      </div>
                      <StatusBadge status={v.status} />
                    </li>
                  ))
                )}
              </ul>
            </div>

            {/* Flagged messages snapshot */}
            <div className="rounded-lg border border-ink/10 bg-sand" data-testid="overview-flagged-panel">
              <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4">
                <div className="flex items-center gap-2 text-ink">
                  <Flag className="h-4 w-4 text-scarlet" strokeWidth={1.75} />
                  <p className="text-sm font-semibold uppercase tracking-wide">
                    Flagged messages
                    {flaggedPending.length > 0 && (
                      <span
                        className="ml-2 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-scarlet px-1.5 text-[10px] font-semibold text-sand"
                        data-testid="overview-flagged-count"
                      >
                        {flaggedPending.length}
                      </span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => onNavigate("flagged")}
                  className="inline-flex items-center gap-1 text-xs font-medium text-ink hover:text-tangerine"
                  data-testid="overview-view-flagged"
                >
                  {flaggedPending.length > 0 ? "Open queue" : "Open"} <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
                </button>
              </div>
              {flaggedPending.length === 0 ? (
                <div className="px-6 py-8 text-center">
                  <p className="font-serif text-lg text-ink">Nothing to review.</p>
                  <p className="mt-1 text-xs text-ink/60">
                    Reported wall messages will appear here.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-ink/10">
                  {flaggedPending.map((r) => {
                    const last = r.reports[r.reports.length - 1];
                    return (
                      <li key={r.id} className="flex items-center justify-between gap-4 px-6 py-4">
                        <div className="min-w-0">
                          <p className="truncate font-medium text-ink">
                            <span className="italic">{r.authorName}</span>{" "}
                            <span className="text-ink/50">· {r.studentFirstName}'s wall</span>
                          </p>
                          <p className="mt-0.5 truncate text-xs text-ink/60">"{r.message}"</p>
                        </div>
                        <div className="flex flex-col items-end gap-1 text-right">
                          <span className="rounded-full bg-scarlet/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-scarlet">
                            {r.reports.length} {r.reports.length === 1 ? "flag" : "flags"}
                          </span>
                          <span className="text-[10px] uppercase tracking-wide text-ink/50">{relTime(last.at)}</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Right column: Recent donations */}
          <div className="rounded-lg border border-ink/10 bg-sand">
            <div className="flex items-center justify-between border-b border-ink/10 px-6 py-4">
              <div className="flex items-center gap-2 text-ink">
                <HandHeart className="h-4 w-4 text-blossom" strokeWidth={1.75} />
                <p className="text-sm font-semibold uppercase tracking-wide">Recent donations</p>
              </div>
              <button
                type="button"
                onClick={() => onNavigate("donations")}
                className="inline-flex items-center gap-1 text-xs font-medium text-ink hover:text-tangerine"
                data-testid="overview-view-donations"
              >
                Open feed <ExternalLink className="h-3 w-3" strokeWidth={1.75} />
              </button>
            </div>
            <ul className="divide-y divide-ink/10">
              {donationsPreview.length === 0 ? (
                <li className="px-6 py-8 text-center">
                  <p className="font-serif text-lg text-ink">No donations yet.</p>
                  <p className="mt-1 text-xs text-ink/60">
                    Gifts to campaigns will appear here as donors give.
                  </p>
                </li>
              ) : (
                donationsPreview.map((d) => (
                  <li key={d.id} className="flex items-center justify-between gap-4 px-6 py-4">
                    <div className="min-w-0">
                      <p className="truncate font-medium text-ink">{d.donor}</p>
                      <p className="mt-0.5 truncate text-xs text-ink/60">
                        to {d.campaign} · {d.school}
                      </p>
                    </div>
                    <span className="font-serif text-lg text-ink">{fmt$(d.amount)}</span>
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </section>

      {/* 3 — Lifetime (will migrate to public landing) */}
      <section className="space-y-6">
        <SectionHeader
          eyebrow="Lifetime numbers"
          title="What we've done together."
          description="These totals will appear on the public landing page once approved. Update the source of truth by editing donation records — no manual entry."
        />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard testid="stat-total-disbursed" label="Total granted" value={fmt$(ADMIN_METRICS.totalDisbursed)} accent="grass" />
          <StatCard testid="stat-students-helped" label="Students helped" value={ADMIN_METRICS.studentsHelped} accent="tangerine" />
          <StatCard testid="stat-avg-gift" label="Average gift" value={fmt$(ADMIN_METRICS.averageGift)} accent="cobalt" />
          <StatCard testid="stat-avg-request" label="Average request" value={fmt$(ADMIN_METRICS.averageRequest)} accent="blossom" />
        </div>
      </section>

      {/* 4 — Top donors */}
      <section className="space-y-6">
        <SectionHeader eyebrow="Top donors" title="Thank the people making this happen." />
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-lg border border-ink/10 bg-sand p-6">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">This month</p>
            {ADMIN_METRICS.topDonorMonth ? (
              <>
                <p className="mt-3 font-serif text-3xl text-ink">{ADMIN_METRICS.topDonorMonth.name}</p>
                <p className="mt-1 text-sm text-ink/60">Total given · {fmt$(ADMIN_METRICS.topDonorMonth.amount)}</p>
                <div className="mt-6">
                  <AdminButton size="sm" variant="secondary" testid="thank-donor-month">
                    <GraduationCap className="h-3.5 w-3.5" strokeWidth={1.75} /> Send thank-you
                  </AdminButton>
                </div>
              </>
            ) : (
              <p className="mt-3 text-sm italic text-ink/50">
                Top donor of the month will appear here once giving opens.
              </p>
            )}
          </div>
          <div className="rounded-lg border border-ink/10 bg-sand p-6">
            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-ink/60">This year</p>
            {ADMIN_METRICS.topDonorYear ? (
              <>
                <p className="mt-3 font-serif text-3xl text-ink">{ADMIN_METRICS.topDonorYear.name}</p>
                <p className="mt-1 text-sm text-ink/60">Total given · {fmt$(ADMIN_METRICS.topDonorYear.amount)}</p>
              </>
            ) : (
              <p className="mt-3 text-sm italic text-ink/50">
                Top donor of the year will appear here once giving opens.
              </p>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};
